import Dialog from "../../components/General/ConfirmableDialog";
import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit, Work, Lock } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useAuth from "../../hooks/useAuth";
import {
  closeOpenPosition,
  createOpenPosition,
  deleteOpenPosition,
  getAllPositions,
  getOpenPositions,
  updateOpenPosition,
} from "../../api/openPositionService";
import {
  CreateOpenPositionDto,
  OpenPositionDto,
  OpenPositionStatus,
} from "../../interfaces/HR/openPosition";

const emptyForm: CreateOpenPositionDto = {
  positionTitle: "",
  department: "",
  jobDescription: "",
  requirements: "",
};

const normalizeOpenPositionStatus = (
  status: OpenPositionDto["status"],
): OpenPositionStatus => {
  if (typeof status === "number") return status;

  const normalizedStatus = status.toString().trim().toLowerCase();
  return normalizedStatus === "open" || normalizedStatus === "0"
    ? OpenPositionStatus.Open
    : OpenPositionStatus.Closed;
};

const isOpenPosition = (position: OpenPositionDto) =>
  normalizeOpenPositionStatus(position.status) === OpenPositionStatus.Open;

const getStatusLabel = (position: OpenPositionDto) =>
  isOpenPosition(position) ? "Open" : "Closed";

const OpenPositionsPage = () => {
  const { token, role } = useAuth();
  const canManagePositions = role?.includes("HR") ?? false;
  const queryClient = useQueryClient();
  const [selectedPosition, setSelectedPosition] =
    useState<OpenPositionDto | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingPosition, setEditingPosition] =
    useState<OpenPositionDto | null>(null);
  const [formValues, setFormValues] =
    useState<CreateOpenPositionDto>(emptyForm);
  const [formStatus, setFormStatus] = useState<OpenPositionStatus>(
    OpenPositionStatus.Open,
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [positionToDelete, setPositionToDelete] =
    useState<OpenPositionDto | null>(null);

  const queryKey = ["openPositions", canManagePositions ? "all" : "open"];
  const {
    data: positions = [],
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: () => {
      if (!token) throw new Error("No token");
      return canManagePositions
        ? getAllPositions(token)
        : getOpenPositions(token);
    },
    enabled: !!token,
  });

  const visiblePositions = useMemo(
    () =>
      canManagePositions
        ? positions
        : positions.filter((position) => isOpenPosition(position)),
    [canManagePositions, positions],
  );

  const refreshPositions = () =>
    queryClient.invalidateQueries({ queryKey: ["openPositions"] });

  const validateForm = () => {
    const missingFields = Object.entries(formValues)
      .filter(([, value]) => !value.trim())
      .map(([key]) => key);
    if (missingFields.length > 0) {
      setFormError(
        "Position title, department, job description, and requirements are required.",
      );
      return false;
    }
    setFormError(null);
    return true;
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!token) throw new Error("No token");
      if (!validateForm()) throw new Error("Validation failed");
      if (editingPosition) {
        return updateOpenPosition(token, {
          ...formValues,
          id: editingPosition.id,
          status: formStatus,
        });
      }
      return createOpenPosition(token, formValues);
    },
    onSuccess: (saved) => {
      setFormOpen(false);
      setEditingPosition(null);
      setFormValues(emptyForm);
      setSelectedPosition(saved);
      refreshPositions();
    },
  });

  const closeMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!token) throw new Error("No token");
      return closeOpenPosition(token, id);
    },
    onSuccess: (closed) => {
      setSelectedPosition(closed);
      refreshPositions();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!token) throw new Error("No token");
      await deleteOpenPosition(token, id);
      return id;
    },
    onSuccess: (id) => {
      if (selectedPosition?.id === id) setSelectedPosition(null);
      setPositionToDelete(null);
      refreshPositions();
    },
  });

  const openCreateForm = () => {
    setEditingPosition(null);
    setFormValues(emptyForm);
    setFormStatus(OpenPositionStatus.Open);
    setFormError(null);
    setFormOpen(true);
  };

  const openEditForm = (position: OpenPositionDto) => {
    setEditingPosition(position);
    setFormValues({
      positionTitle: position.positionTitle,
      department: position.department,
      jobDescription: position.jobDescription,
      requirements: position.requirements,
    });
    setFormStatus(normalizeOpenPositionStatus(position.status));
    setFormError(null);
    setFormOpen(true);
  };

  const mutationError =
    saveMutation.error || closeMutation.error || deleteMutation.error;

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        flexWrap={{ xs: "wrap", lg: "nowrap" }}
        useFlexGap
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Open Positions
          </Typography>
          <Typography color="text.secondary">
            Browse active internal opportunities and view full position details.
          </Typography>
        </Box>
        {canManagePositions && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openCreateForm}
          >
            New Position
          </Button>
        )}
      </Stack>

      {mutationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Action failed. Please try again.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Unable to load open positions.
        </Alert>
      )}

      {isLoading ? (
        <Stack alignItems="center" py={8}>
          <CircularProgress />
        </Stack>
      ) : visiblePositions.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6">No open positions right now.</Typography>
            <Typography color="text.secondary">
              Closed positions are hidden from the default list.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={5} lg={4}>
            <Stack spacing={2}>
              {visiblePositions.map((position) => (
                <Card
                  key={position.id}
                  variant={
                    selectedPosition?.id === position.id
                      ? "elevation"
                      : "outlined"
                  }
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSelectedPosition(position)}
                >
                  <CardContent>
                    <Stack
                      flexWrap={{ xs: "wrap", lg: "nowrap" }}
                      useFlexGap
                      direction="row"
                      justifyContent="space-between"
                      gap={1}
                    >
                      <Box>
                        <Typography variant="h6">
                          {position.positionTitle}
                        </Typography>
                        <Typography color="text.secondary">
                          {position.department}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        color={isOpenPosition(position) ? "success" : "default"}
                        label={getStatusLabel(position)}
                      />
                    </Stack>
                    {isOpenPosition(position) && (
                      <Button
                        component="a"
                        variant="contained"
                        size="small"
                        href={`/candidate?position=${encodeURIComponent(position.positionTitle)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        sx={{ mt: 2 }}
                      >
                        Apply
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12} md={7} lg={8}>
            {selectedPosition ? (
              <Card>
                <CardContent>
                  <Stack
                    flexWrap={{ xs: "wrap", lg: "nowrap" }}
                    useFlexGap
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={2}
                  >
                    <Box>
                      <Typography variant="h5" fontWeight={700}>
                        {selectedPosition.positionTitle}
                      </Typography>
                      <Typography color="text.secondary">
                        {selectedPosition.department}
                      </Typography>
                    </Box>
                    <Stack
                      flexWrap={{ xs: "wrap", lg: "nowrap" }}
                      useFlexGap
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <Chip
                        color={
                          isOpenPosition(selectedPosition)
                            ? "success"
                            : "default"
                        }
                        label={getStatusLabel(selectedPosition)}
                      />
                      {isOpenPosition(selectedPosition) && (
                        <Button
                          component="a"
                          variant="contained"
                          size="small"
                          href={`/candidate?position=${encodeURIComponent(selectedPosition.positionTitle)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Apply
                        </Button>
                      )}
                      {canManagePositions && (
                        <>
                          <IconButton
                            aria-label="Edit position"
                            onClick={() => openEditForm(selectedPosition)}
                          >
                            <Edit />
                          </IconButton>
                          {isOpenPosition(selectedPosition) && (
                            <Button
                              size="small"
                              onClick={() =>
                                closeMutation.mutate(selectedPosition.id)
                              }
                            >
                              Close
                            </Button>
                          )}
                          <IconButton
                            aria-label="Delete position"
                            color="error"
                            onClick={() =>
                              setPositionToDelete(selectedPosition)
                            }
                          >
                            <Delete />
                          </IconButton>
                        </>
                      )}
                    </Stack>
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight={700}>
                    Job Description
                  </Typography>
                  <Typography whiteSpace="pre-line" mb={2}>
                    {selectedPosition.jobDescription}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Requirements
                  </Typography>
                  <Typography whiteSpace="pre-line" mb={2}>
                    {selectedPosition.requirements}
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent>
                  <Stack alignItems="center" py={6} spacing={1}>
                    <Work color="disabled" />
                    <Typography>
                      Select a position to view full details.
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      )}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingPosition ? "Edit Position" : "Create Position"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {formError && <Alert severity="warning">{formError}</Alert>}
            <TextField
              required
              label="Position title"
              value={formValues.positionTitle}
              onChange={(e) =>
                setFormValues({ ...formValues, positionTitle: e.target.value })
              }
            />
            <TextField
              required
              label="Department/team"
              value={formValues.department}
              onChange={(e) =>
                setFormValues({ ...formValues, department: e.target.value })
              }
            />
            <TextField
              required
              multiline
              minRows={4}
              label="Job description"
              value={formValues.jobDescription}
              onChange={(e) =>
                setFormValues({ ...formValues, jobDescription: e.target.value })
              }
            />
            <TextField
              required
              multiline
              minRows={4}
              label="Requirements"
              value={formValues.requirements}
              onChange={(e) =>
                setFormValues({ ...formValues, requirements: e.target.value })
              }
            />
            {editingPosition && (
              <TextField
                select
                label="Status"
                value={formStatus}
                onChange={(e) =>
                  setFormStatus(Number(e.target.value) as OpenPositionStatus)
                }
              >
                <MenuItem value={OpenPositionStatus.Open}>Open</MenuItem>
                <MenuItem value={OpenPositionStatus.Closed}>Closed</MenuItem>
              </TextField>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!positionToDelete}
        onClose={() => setPositionToDelete(null)}
      >
        <DialogTitle>Delete Position?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {positionToDelete?.positionTitle}?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPositionToDelete(null)}>No</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() =>
              positionToDelete && deleteMutation.mutate(positionToDelete.id)
            }
            disabled={deleteMutation.isPending}
          >
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OpenPositionsPage;
